import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SLOTS, SLOT_LEGEND_GROUPS, STATUS_OPTIONS, STATUS_LABEL } from "../lib/slots";
import { inclusiveDays } from "../lib/calendar";
import { fmtDateDE } from "../lib/finance";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { LogOut, Plus, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, ChevronsDownUp, Settings as SettingsIcon, PanelLeftClose } from "lucide-react";

export default function Sidebar({
  projects,
  statusFilter,
  onStatusFilter,
  statusCounts,
  year,
  onPrevYear,
  onNextYear,
  onCreate,
  onSelectProject,
  onLogout,
  onCollapse,
  ownerEmail,
  expanded = true,
  onExpandedChange,
}) {
  const counts = statusCounts || {};
  const [legendOpen, setLegendOpen] = useState(false);
  const selected = Array.isArray(statusFilter) ? statusFilter : [];
  const selectedCount = selected.reduce((acc, v) => acc + (counts[v] ?? 0), 0);
  const triggerLabel = (() => {
    if (selected.length === 0) return "Kein Status (0)";
    if (selected.length >= STATUS_OPTIONS.length) return `Alle Status (${counts.all ?? 0})`;
    if (selected.length === 1) {
      const opt = STATUS_OPTIONS.find((s) => s.value === selected[0]);
      return opt ? `${opt.label} (${counts[selected[0]] ?? 0})` : "";
    }
    return `${selected.length} Status (${selectedCount})`;
  })();
  const toggleStatus = (val) => {
    const isCurrentlySelected = selected.includes(val);
    let next;
    if (isCurrentlySelected) {
      next = selected.filter((v) => v !== val);
    } else if (val === "active") {
      // For planning purposes "In Aufbau" and "Live" usually belong together —
      // auto-add "live" when "active" is newly selected. The user can still
      // manually unselect "live" afterwards.
      const set = new Set(selected);
      set.add("active");
      set.add("live");
      next = [...set];
    } else {
      next = [...selected, val];
    }
    onStatusFilter(next);
  };
  return (
    <aside
      className="w-[300px] shrink-0 h-screen sticky top-0 border-r border-slate-200 bg-white flex flex-col"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-6 pt-6 pb-5 border-b border-slate-100 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase text-slate-400 mb-1">
            Kapazitätsplaner
          </div>
          <div
            className="text-xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, ui-sans-serif" }}
          >
            Wandplaner
          </div>
        </div>
        {onCollapse && (
          <button
            onClick={onCollapse}
            className="h-7 w-7 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-500 shrink-0"
            aria-label="Sidebar einklappen"
            data-testid="sidebar-collapse-button"
          >
            <PanelLeftClose className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Year Nav */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <button
          onClick={onPrevYear}
          className="h-8 w-8 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="prev-year-button"
          aria-label="Vorheriges Jahr"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div
          className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900"
          style={{ fontFamily: "Manrope, ui-sans-serif" }}
          data-testid="current-year-label"
        >
          {year}
        </div>
        <button
          onClick={onNextYear}
          className="h-8 w-8 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="next-year-button"
          aria-label="Nächstes Jahr"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Create */}
      <div className="px-6 py-4 border-b border-slate-100">
        <Button
          onClick={() => onCreate?.()}
          className="w-full h-9 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="create-project-button"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Projekt erstellen
        </Button>
      </div>

      {/* Filter */}
      <div className="px-6 py-4 border-b border-slate-100">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
          Statusfilter
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="h-9 w-full inline-flex items-center justify-between gap-2 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="project-status-filter-trigger"
            >
              <span className="truncate">{triggerLabel}</span>
              <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[252px] p-2">
            <div className="flex items-baseline justify-between px-1 py-1">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">
                Status
              </div>
              <div className="flex items-baseline gap-2">
                <button
                  type="button"
                  onClick={() => onStatusFilter(STATUS_OPTIONS.map((o) => o.value))}
                  className="text-[10px] text-slate-500 hover:text-slate-800"
                  data-testid="project-status-filter-clear"
                >
                  Alle anzeigen
                </button>
                <button
                  type="button"
                  onClick={() => onStatusFilter([])}
                  className="text-[10px] text-slate-500 hover:text-slate-800"
                  data-testid="project-status-filter-deselect-all"
                >
                  Alle abwählen
                </button>
              </div>
            </div>
            <div className="grid gap-1">
              {STATUS_OPTIONS.map((s) => {
                const checked = selected.includes(s.value);
                return (
                  <label
                    key={s.value}
                    className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-slate-50 cursor-pointer"
                    data-testid={`project-status-filter-option-${s.value}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleStatus(s.value)}
                    />
                    <span className="text-sm text-slate-700 flex-1">{s.label}</span>
                    <span className="text-xs text-slate-400 tabular-nums">{counts[s.value] ?? 0}</span>
                  </label>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="px-3 mb-2 flex items-center justify-between gap-2">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
            Projekte ({projects.length})
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onExpandedChange?.(true)}
              disabled={expanded}
              className="p-1 rounded-sm text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
              title="Alle aufklappen"
              aria-label="Alle aufklappen"
              data-testid="sidebar-expand-all-button"
            >
              <ChevronsUpDown className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onExpandedChange?.(false)}
              disabled={!expanded}
              className="p-1 rounded-sm text-slate-400 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
              title="Alle einklappen"
              aria-label="Alle einklappen"
              data-testid="sidebar-collapse-all-button"
            >
              <ChevronsDownUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {projects.length === 0 ? (
          <div className="px-3 py-6 text-xs text-slate-400">
            Keine Projekte. Klicke auf eine leere Zelle im Planer oder „Projekt erstellen".
          </div>
        ) : (
          <ul className="space-y-1">
            {projects.map((p) => {
              const slot = SLOTS[p.slot - 1];
              const durationDays = inclusiveDays(p.start_date, p.end_date);
              return (
                <li key={p.id}>
                  <button
                    onClick={() => onSelectProject?.(p)}
                    className="w-full text-left px-3 py-2 rounded-sm hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors group"
                    data-testid={`project-sidebar-item-${p.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className="mt-1.5 w-2.5 h-2.5 rounded-sm shrink-0 border border-black/10"
                        style={{ backgroundColor: slot?.color }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {p.name}
                          {durationDays != null && (
                            <span className="ml-1 text-slate-400 font-normal tabular-nums">({durationDays})</span>
                          )}
                        </div>
                        {expanded && (
                          <>
                            <div className="text-xs text-slate-500 truncate">{p.client}</div>
                            <div className="text-[10px] text-slate-400 mt-0.5 tabular-nums">
                              {fmtDateDE(p.start_date)} → {fmtDateDE(p.end_date)} · {STATUS_LABEL[p.status]}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Slot legend */}
      <div className="px-6 py-4 border-t border-slate-100">
        <button
          type="button"
          onClick={() => setLegendOpen((v) => !v)}
          className="w-full flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-slate-500 hover:text-slate-800"
          aria-expanded={legendOpen}
          data-testid="slot-legend-toggle"
        >
          <span>Slot-Legende</span>
          {legendOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {legendOpen && (
          <div className="grid grid-rows-6 grid-flow-col gap-x-4 gap-y-1.5 mt-2">
            {SLOT_LEGEND_GROUPS.map((g) => (
              <div key={g.color} className="flex items-center gap-2 text-xs text-slate-600">
                <span
                  className="w-3 h-3 rounded-sm border border-black/10 shrink-0"
                  style={{ backgroundColor: g.color }}
                />
                <span className="tabular-nums text-[11px]">{g.slotNumbers.join(" · ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="text-[10px] text-slate-400 truncate flex-1 min-w-0" title={ownerEmail}>
          {ownerEmail}
        </div>
        <Link
          to="/settings"
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="settings-link"
          title="Konto-Einstellungen"
        >
          <SettingsIcon className="w-3.5 h-3.5" />
        </Link>
        <button
          onClick={onLogout}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="logout-button"
        >
          <LogOut className="w-3.5 h-3.5" />
          Abmelden
        </button>
      </div>
    </aside>
  );
}
